import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import FilePreviewer from '@components/FilePreviewer';
import LoaderComponent from '@components/LoaderComponent';
import NoData from '@components/NoData';
import { toastError, toastSuccess } from '@components/Toast';
import TopGallerySlider from '@components/TopGallerySlider';

import { fetchOrderDetailsInfo } from '@store/orders/actions';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	convertNumberToUSFormat,
	getShortName,
	onUploadFile,
	packTag,
	printApiError,
	renderSetImages,
	setTag,
	STATIC_SET_CONSTANTS,
	validateNumber
} from '@utils/Util';

const CommercialInvoice = ({ orderId, poItemIdentifier }) => {
	const [supplierList, setSupplierList] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [invoiceDetails, setInvoiceDetails] = useState([]);
	const [errors, setErrors] = useState({});
	const [editId, setEditId] = useState(null);
	const [isNewRow, setIsNewRow] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		getSupplierList(orderId, true);
	}, [orderId]);

	const getSupplierList = async (orderId, isInitialRender = false) => {
		setIsLoading(true);
		await ApiClient.GET('getSupplierListsOfCommercialInvoice', orderId)
			.then(({ data }) => {
				setIsLoading(false);
				setSupplierList(data);
				if (isInitialRender) {
					getInvoiceDetailsInfo(data[0].id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const getInvoiceDetailsInfo = async (invoiceId) => {
		setIsLoading(true);
		await ApiClient.GET('getCommercialInvoiceDetails', invoiceId)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data?.commercialInvoiceProducts) {
					const updatedProducts = data?.commercialInvoiceProducts?.map((item) => {
						if (!item.productShipmentDTOList) {
							item.productShipmentDTOList = [];
						}

						if (item.productShipmentDTOList.length === 0) {
							item.productShipmentDTOList.push({
								uid: _getKey(),
								rfqId: item?.rfqId,
								quantity: '',
								invoiceNo: '',
								amount: '',
								invoiceDate: '',
								factoryCost: '',
								poNumber: item?.poNumber || '',
								factoryAmount: item?.factoryAmount || '',
								factoryQuantity: item?.factoryQuantity || ''
							});
						} else {
							item.productShipmentDTOList = item.productShipmentDTOList.map((subItem) => {
								subItem['uid'] = _getKey();
								subItem['poItemIdentifierCode'] = subItem?.poItemIdentifierCode;
								subItem.rfqId = item?.rfqId;
								subItem.commercialInvoiceId = item?.id;
								subItem.factoryCost = subItem?.factoryCost || '';
								subItem.poNumber = item?.poNumber || '';
								subItem.factoryAmount = item?.factoryAmount || '';
								subItem.factoryQuantity = item?.factoryQuantity || '';
								return subItem;
							});
						}

						return item;
					});

					const updatedData = { ...data, commercialInvoiceProducts: updatedProducts };
					setInvoiceDetails(updatedData);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const isValidShipment = (item) => {
		let errors = {};
		if (!item?.invoiceNo) {
			errors.invoiceError = true;
		}
		// if (!item?.quantity) {
		// 	errors.quantityError = true;
		// }
		// if (!item?.buyerUnitPrice) {
		// 	errors.buyerUnitPriceError = true;
		// }
		// if (!item?.amount) {
		// 	errors.amountError = true;
		// }
		// if (!item?.factoryUnitPrice) {
		// 	errors.factoryUnitPriceError = true;
		// }
		// if (!item?.factoryCost) {
		// 	errors.factoryCostError = true;
		// }
		if (!item?.invoiceDate) {
			errors.invoiceDateError = true;
		}
		if (Object.keys(errors).length > 0) {
			errors.uid = editId;
			setErrors(errors);
			return false;
		} else {
			setErrors({});
			return true;
		}
	};

	const addShipmentRow = (product, shipment) => {
		setIsNewRow(true);
		const updatedProducts = invoiceDetails?.commercialInvoiceProducts?.map((item) => {
			if (
				Array.isArray(item?.productShipmentDTOList) &&
				item?.productId === product.productId &&
				item.rfqId === shipment.rfqId
			) {
				const uniqueId = _getKey();

				const selectedIndex = item.productShipmentDTOList.findIndex(
					(obj) => obj.uid === shipment.uid
				);

				item?.productShipmentDTOList.splice(selectedIndex + 1, 0, {
					uid: uniqueId,
					quantity: '',
					invoiceNo: '',
					amount: '',
					invoiceDate: '',
					factoryCost: ''
				});
				setEditId(uniqueId);
			}
			return item;
		});
		const updatedData = { ...invoiceDetails, commercialInvoiceProducts: updatedProducts };
		setInvoiceDetails(updatedData);
	};

	const removeShipmentRow = (product, shipment) => {
		if (isNewRow && invoiceDetails && invoiceDetails?.commercialInvoiceProducts) {
			const updatedProducts = invoiceDetails?.commercialInvoiceProducts?.map((item) => {
				if (Array.isArray(item?.productShipmentDTOList) && item?.id === product.id) {
					item.productShipmentDTOList = item?.productShipmentDTOList?.filter((subItem) => {
						return subItem.uid !== shipment.uid;
					});
				}
				return item;
			});

			const updatedData = { ...invoiceDetails, commercialInvoiceProducts: updatedProducts };
			setInvoiceDetails(updatedData);
		}
		setEditId(null);
		setIsNewRow(false);
	};

	const onUpdateInvoiceDetailsInfo = async (item, shipment) => {
		const productShipmentDTOList = invoiceDetails?.commercialInvoiceProducts.find(
			(invoice) => invoice.id === shipment.commercialInvoiceId
		)?.productShipmentDTOList;
		let body = productShipmentDTOList.find((product) => product.uid === shipment.uid);

		const isValid = isValidShipment(body);
		if (!isValid) return;

		setIsLoading(true);
		await ApiClient.POST(
			'updateCommercialInvoiceProductShipmentsQuantity',
			[body],
			`${shipment.commercialInvoiceId}/update-shipment-unit`
		)
			.then(({ data }) => {
				setIsLoading(false);
				toastSuccess(data.message);
				getInvoiceDetailsInfo(invoiceDetails?.id);
				setIsNewRow(false);
				onCloseQuantityModal();
			})
			.catch((error) => {
				setIsLoading(false);
				printApiError(error);
			});
	};

	const onCloseQuantityModal = () => {
		setEditId(null);
		setErrors({});
	};

	const onInvoiceCardClick = (item) => {
		if (invoiceDetails.id !== item.id) {
			getInvoiceDetailsInfo(item.id);
			onCloseQuantityModal();
		}
	};

	const onCompleteCommercialInvoice = async (id) => {
		setIsLoading(true);
		await ApiClient.PUT('completeCommercialInvoices', {}, id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data.success) {
					dispatch(fetchOrderDetailsInfo(orderId));
					getInvoiceDetailsInfo(invoiceDetails?.id);
					getSupplierList(orderId);
				}
			})
			.catch((error) => {
				setIsLoading(false);
				printApiError(error);
			});
	};

	const onChangeShipment = (e, item, subItem, commercialInvoiceId) => {
		let { value, name } = e.target;
		let details = { ...invoiceDetails };
		details.commercialInvoiceProducts.forEach((product) => {
			if (product.id === commercialInvoiceId) {
				product.productShipmentDTOList.forEach((shipmentItem) => {
					if (name === 'invoiceNo' && shipmentItem.uid === subItem.uid) {
						shipmentItem.invoiceNo = value;
					} else if (name === 'quantity' && shipmentItem.uid === subItem.uid) {
						shipmentItem.quantity = value;
					} else if (name === 'amount' && shipmentItem.uid === subItem.uid) {
						shipmentItem.amount = value;
					} else if (name === 'invoiceDate' && shipmentItem.uid === subItem.uid) {
						shipmentItem.invoiceDate = value;
					} else if (name === 'factoryCost' && shipmentItem.uid === subItem.uid) {
						shipmentItem.factoryCost = value;
					} else if (name === 'buyerUnitPrice' && shipmentItem.uid === subItem.uid) {
						shipmentItem.buyerUnitPrice = value;
					} else if (name === 'factoryUnitPrice' && shipmentItem.uid === subItem.uid) {
						shipmentItem.factoryUnitPrice = value;
					}

					if (
						name !== 'amount' &&
						shipmentItem.uid === subItem.uid &&
						shipmentItem.buyerUnitPrice &&
						shipmentItem.quantity &&
						(name === 'buyerUnitPrice' || name === 'quantity')
					) {
						shipmentItem.amount = shipmentItem.buyerUnitPrice * shipmentItem.quantity;
					}
					if (
						name !== 'factoryCost' &&
						shipmentItem.uid === subItem.uid &&
						shipmentItem.factoryUnitPrice &&
						shipmentItem.quantity &&
						(name === 'factoryUnitPrice' || name === 'quantity')
					) {
						shipmentItem.factoryCost = shipmentItem.factoryUnitPrice * shipmentItem.quantity;
					}
				});
			}
		});
		setInvoiceDetails(details);
	};
	const onDeleteInvoice = async (id) => {
		setIsLoading(true);
		await ApiClient.DELETE(
			'getSupplierListsOfCommercialInvoice',
			{},
			`${invoiceDetails?.id}/document/${id}`
		)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					getInvoiceDetailsInfo(invoiceDetails?.id);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onUploaDocument = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('updateCommercialInfoDocument', [data], `${invoiceDetails?.id}/document`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.message) {
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const groupedData = invoiceDetails?.commercialInvoiceProducts?.reduce((acc, item) => {
		const { productId, estimatedDeliveryDate, productShipmentDTOList, ...rest } = item;

		if (!acc[productId]) {
			acc[productId] = {
				productId,
				etd: {},
				...rest
			};
		}

		if (!acc[productId].etd[estimatedDeliveryDate]) {
			acc[productId].etd[estimatedDeliveryDate] = [];
		}

		if (productShipmentDTOList && productShipmentDTOList.length > 0) {
			acc[productId].etd[estimatedDeliveryDate].push(
				...productShipmentDTOList.map((shipment) => ({
					...shipment,
					estimatedDeliveryDate,
					commercialInvoiceId: item.id
				}))
			);
		}

		return acc;
	}, {});

	const result = groupedData ? Object.values(groupedData) : [];

	const sumQtyAndPrice = (productId) => {
		const filteredData = invoiceDetails?.commercialInvoiceProducts?.filter(
			(item) => item.productId === productId
		);
		let totalFactoryAmount = 0;
		let totalFactoryQuantity = 0;

		filteredData.forEach((item) => {
			totalFactoryAmount += item.factoryAmount;
			totalFactoryQuantity += item.factoryQuantity;
		});

		return {
			totalFactoryAmount,
			totalFactoryQuantity
		};
	};

	const renderActionButtons = (product, shipment) => {
		return (
			<>
				{!isNewRow && editId === shipment.uid ? (
					<>
						<button
							className="button text size32 p-0 ml-2"
							onClick={() => onUpdateInvoiceDetailsInfo(product, shipment, 'edit')}
							disabled={isLoading}
						>
							<span className="primary_bright_01">Update</span>
						</button>
						<button
							className="button text size32 p-0 ml-2"
							onClick={() => {
								setEditId(null);
								getInvoiceDetailsInfo(invoiceDetails?.id);
							}}
						>
							<span className="gray_dark_02">Cancel</span>
						</button>
					</>
				) : (
					<>
						<div>
							{isNewRow && editId === shipment.uid ? (
								<>
									<button
										className="button text size32 p-0"
										onClick={() => {
											onUpdateInvoiceDetailsInfo(product, shipment);
										}}
									>
										<span className="primary_bright_01">Save</span>
									</button>
									<button
										className="button text size32 p-0"
										onClick={() => removeShipmentRow(product, shipment)}
									>
										<span className="gray_dark_02">Cancel</span>
									</button>
								</>
							) : (
								<>
									<button
										className="button text size32 p-0"
										onClick={() => setEditId(shipment.uid)}
										disabled={isNewRow}
									>
										<span className="primary_bright_01">Edit</span>
									</button>
									{shipment.id && (
										<button
											className="button text size32 p-0"
											onClick={() => addShipmentRow(product, shipment)}
											disabled={isNewRow}
										>
											<span className="primary_bright_01">Add Shipment</span>
										</button>
									)}
								</>
							)}
						</div>
					</>
				)}
			</>
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="create-order-container">
				{supplierList.length === 0 ? (
					<NoData message={'Please assign a supplier first'} />
				) : (
					<>
						<TopGallerySlider
							itemList={supplierList}
							details={invoiceDetails}
							onCardClick={onInvoiceCardClick}
							type={'COMMERCIAL_INVOICE'}
						/>
						<div className="commercial-update-info-section">
							<div className="upload-commercial-invoices">
								<div className="title">
									<h2 className="semibold-16">Update commercial invoice info</h2>
								</div>
								<div className="upload-cm-invoices mt-1 ">
									<div className="upload-cm">
										<label htmlFor="cm-upload" className="drag-upload">
											<img
												src={`${process.env.PUBLIC_URL}/icons/upload-sm.svg`}
												alt="upload"
											/>
											<p className="regular-14 primary_bright_01">Invoice</p>
										</label>
										<input
											type="file"
											className="file-upload d-none"
											onChange={(e) =>
												onUploadFile(
													e,
													'ACCESSORIES_DESIGN',
													onUploaDocument,
													'MULTI'
												)
											}
											name="cm-upload"
											id="cm-upload"
											multiple
											accept="*"
										/>
									</div>
									<div className="uploaded-cm">
										<div className="cm-invoice-list d-flex">
											{invoiceDetails &&
												invoiceDetails?.documents?.map((item) => (
													<div className="single-cm" key={item.id}>
														<div className="cm-name">
															<FilePreviewer docUrl={item?.docUrl} />
															<Tooltip title={item?.name} placement="top" arrow>
																<a
																	target="_blank"
																	rel="noreferrer"
																	href={item?.docUrl}
																	className="regular-12 primary_bright_01 cursor-pointer"
																>
																	{getShortName(item?.name, 15)}
																</a>
															</Tooltip>
														</div>
														<img
															src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
															alt="delete"
															className="delete-cm"
															onClick={() => onDeleteInvoice(item.id)}
														/>
													</div>
												))}
										</div>
									</div>
								</div>
								<div className="cm-products-table-container scroll-x-label">
									<table className="table">
										<thead>
											<tr>
												<th>Design</th>
												<th>ETD</th>
												<th>PO Details</th>
												<th>{poItemIdentifier || 'SKU'}</th>
												<th>Commercial Invoice Number</th>
												<th>Date</th>
												<th>Shipped Qty (pcs)</th>
												<th>Buyer Unit Price</th>
												<th>Invoice Amount</th>
												<th>Factory Unit Price</th>
												<th>Factory Cost</th>
												<th>More</th>
											</tr>
										</thead>
										<tbody>
											{result?.map((product) => {
												const { totalFactoryAmount, totalFactoryQuantity } =
													sumQtyAndPrice(product.productId);
												return (
													<React.Fragment key={product.productId}>
														{Object.entries(product.etd).map(
															([etd, shipments], etdIndex) => {
																return (
																	<React.Fragment
																		key={`${product.productId}-${etd}`}
																	>
																		{shipments.map(
																			(shipment, shipmentIndex) => (
																				<tr key={shipment.id}>
																					{etdIndex === 0 &&
																						shipmentIndex ===
																							0 && (
																							<td
																								className="design-details-column"
																								rowSpan={
																									Object.values(
																										product.etd
																									).flat()
																										.length
																								}
																							>
																								<div className="design-details">
																									{product?.setPackStyleFlag ===
																									STATIC_SET_CONSTANTS.SET_PRODUCT ? (
																										renderSetImages(
																											product?.setMemberDocumentList,
																											'56px'
																										)
																									) : (
																										<img
																											src={
																												product
																													?.document
																													?.docUrl
																											}
																											alt
																											className="cell-img pr-2"
																										/>
																									)}
																									<span className="ml-1">
																										<div>
																											{product?.setPackStyleFlag ===
																												STATIC_SET_CONSTANTS.SET_PRODUCT &&
																												setTag()}
																											{product?.setPackStyleFlag ===
																												STATIC_SET_CONSTANTS.PACK_PRODUCT &&
																												packTag()}
																										</div>
																										<span className="semibold-14 mb-1">
																											{
																												product.name
																											}{' '}
																										</span>
																										<ul className="common-list-component">
																											<li>
																												PO:{' '}
																												{
																													product.poNumber
																												}
																											</li>
																											<li>
																												{
																													totalFactoryQuantity
																												}{' '}
																												units
																											</li>
																											<li>
																												$
																												{convertNumberToUSFormat(
																													totalFactoryAmount
																												)}{' '}
																											</li>
																										</ul>
																									</span>
																									{product?.shipmentStatus ===
																										'COMPLETED' && (
																										<img
																											src={`${process.env.PUBLIC_URL}/icons/Success.svg`}
																											alt="Shiped"
																											className="product-shipped"
																										/>
																									)}
																									<div className="shipped-btn">
																										{product?.shipmentStatus ===
																											'PENDING' && (
																											<div className="approve-btn">
																												<button
																													className="button approve size32"
																													onClick={() =>
																														onCompleteCommercialInvoice(
																															product.id
																														)
																													}
																													disabled={
																														product?.totalShippedQuantity <=
																															0 ||
																														isNewRow
																													}
																												>
																													Complete
																												</button>
																											</div>
																										)}
																									</div>
																								</div>
																							</td>
																						)}

																					{/* ETD Column */}
																					{shipmentIndex === 0 && (
																						<td
																							rowSpan={
																								shipments.length
																							}
																						>
																							<span className="gray_dark_01 text-nowrap">
																								{etd}
																							</span>
																						</td>
																					)}
																					<td>
																						<p className="gray_dark_01">
																							PO:{' '}
																							{
																								shipment.poNumber
																							}
																						</p>
																						<p className="gray_dark_01">
																							{shipment.factoryAmount
																								? `$${convertNumberToUSFormat(
																										shipment.factoryAmount
																									)}`
																								: ''}
																						</p>
																						<p className="gray_dark_01">
																							{
																								shipment.factoryQuantity
																							}{' '}
																							units
																						</p>
																					</td>
																					{/* Shipment details */}
																					<td>
																						<span className="gray_dark_01">
																							{
																								shipment.poItemIdentifierCode
																							}
																						</span>
																					</td>
																					<td>
																						{editId ===
																						shipment.uid ? (
																							<input
																								className={`${
																									errors.uid ===
																										shipment.uid &&
																									errors.invoiceError &&
																									'common-border-error'
																								}`}
																								autoFocus
																								type="text"
																								autoComplete="off"
																								name="invoiceNo"
																								value={
																									shipment?.invoiceNo
																								}
																								onChange={(
																									e
																								) =>
																									onChangeShipment(
																										e,
																										product,
																										shipment,
																										shipment.commercialInvoiceId
																									)
																								}
																							/>
																						) : (
																							<span className="gray_dark_01">
																								{
																									shipment?.invoiceNo
																								}
																							</span>
																						)}
																					</td>
																					<td>
																						<span className="gray_dark_01">
																							{editId ===
																							shipment.uid ? (
																								<input
																									className={`${
																										errors.uid ===
																											shipment.uid &&
																										errors.invoiceDateError &&
																										'common-border-error'
																									}`}
																									type="date"
																									name="invoiceDate"
																									value={
																										shipment?.invoiceDate
																									}
																									onChange={(
																										e
																									) =>
																										onChangeShipment(
																											e,
																											product,
																											shipment,
																											shipment.commercialInvoiceId
																										)
																									}
																								/>
																							) : (
																								<span className="gray_dark_01">
																									{shipment.invoiceDate
																										? changeDateFormat(
																												shipment.invoiceDate,
																												'',
																												'Do MMM, YY'
																											)
																										: ''}
																								</span>
																							)}
																						</span>
																					</td>
																					<td>
																						{/* <span className="gray_dark_01">
																				{shipment.quantity}
																			</span> */}
																						{editId ===
																						shipment.uid ? (
																							<input
																								className={`${
																									errors.uid ===
																										shipment.uid &&
																									errors.quantityError &&
																									'common-border-error'
																								}`}
																								type="number"
																								name="quantity"
																								min={0}
																								value={
																									shipment?.quantity
																								}
																								onChange={(
																									e
																								) =>
																									onChangeShipment(
																										e,
																										product,
																										shipment,
																										shipment.commercialInvoiceId
																									)
																								}
																								onKeyPress={
																									validateNumber
																								}
																							/>
																						) : (
																							<span className="gray_dark_01">
																								{
																									shipment?.quantity
																								}
																							</span>
																						)}
																					</td>

																					<td>
																						{/* <span className="gray_dark_01">
																				{shipment.amount &&
																					`$${shipment.amount}`}
																			</span> */}
																						{editId ===
																						shipment.uid ? (
																							<input
																								className={`${
																									errors.uid ===
																										shipment.uid &&
																									errors.buyerUnitPriceError &&
																									'common-border-error'
																								}`}
																								type="number"
																								step="any"
																								name="buyerUnitPrice"
																								min={0}
																								value={
																									shipment?.buyerUnitPrice
																								}
																								onChange={(
																									e
																								) =>
																									onChangeShipment(
																										e,
																										product,
																										shipment,
																										shipment.commercialInvoiceId
																									)
																								}
																							/>
																						) : (
																							<span className="gray_dark_01">
																								{shipment?.buyerUnitPrice &&
																									`$${shipment?.buyerUnitPrice}`}
																							</span>
																						)}
																					</td>

																					<td>
																						{/* <span className="gray_dark_01">
																				{shipment.amount &&
																					`$${shipment.amount}`}
																			</span> */}
																						{editId ===
																						shipment.uid ? (
																							<input
																								className={`${
																									errors.uid ===
																										shipment.uid &&
																									errors.amountError &&
																									'common-border-error'
																								}`}
																								type="number"
																								step="any"
																								name="amount"
																								min={0}
																								value={
																									shipment?.amount
																								}
																								onChange={(
																									e
																								) =>
																									onChangeShipment(
																										e,
																										product,
																										shipment,
																										shipment.commercialInvoiceId
																									)
																								}
																							/>
																						) : (
																							<span className="gray_dark_01">
																								{shipment?.amount &&
																									`$${shipment?.amount}`}
																							</span>
																						)}
																					</td>
																					<td>
																						<span className="gray_dark_01">
																							{editId ===
																							shipment.uid ? (
																								<input
																									className={`${
																										errors.uid ===
																											shipment.uid &&
																										errors.factoryUnitPriceError &&
																										'common-border-error'
																									}`}
																									min={0}
																									type="number"
																									name="factoryUnitPrice"
																									value={
																										shipment?.factoryUnitPrice
																									}
																									onChange={(
																										e
																									) =>
																										onChangeShipment(
																											e,
																											product,
																											shipment,
																											shipment.commercialInvoiceId
																										)
																									}
																								/>
																							) : (
																								<span className="gray_dark_01">
																									{shipment?.factoryUnitPrice >
																										0 &&
																										`$${shipment?.factoryUnitPrice}`}
																								</span>
																							)}
																						</span>
																					</td>
																					<td>
																						<span className="gray_dark_01">
																							{editId ===
																							shipment.uid ? (
																								<input
																									className={`${
																										errors.uid ===
																											shipment.uid &&
																										errors.factoryCostError &&
																										'common-border-error'
																									}`}
																									min={0}
																									type="number"
																									name="factoryCost"
																									value={
																										shipment?.factoryCost
																									}
																									onChange={(
																										e
																									) =>
																										onChangeShipment(
																											e,
																											product,
																											shipment,
																											shipment.commercialInvoiceId
																										)
																									}
																								/>
																							) : (
																								<span className="gray_dark_01">
																									{shipment?.factoryCost >
																										0 &&
																										`$${shipment?.factoryCost}`}
																								</span>
																							)}
																						</span>
																					</td>
																					<td>
																						{renderActionButtons(
																							product,
																							shipment,
																							shipmentIndex
																						)}
																					</td>
																				</tr>
																			)
																		)}
																	</React.Fragment>
																);
															}
														)}
													</React.Fragment>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</LoaderComponent>
	);
};

export default CommercialInvoice;
